import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Coins, ArrowRight, Lock, Sparkles, Info, Check } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const JARS = [
  { id: 'spend', name: 'Spend Jar', emoji: '💰', color: 'bg-blue-100', description: '+1 bonus per 10 coins added!' },
  { id: 'save', name: 'Save Jar', emoji: '🏦', color: 'bg-green-100', description: '+5% interest every 3 days!' },
  { id: 'dream', name: 'Dream Jar', emoji: '⭐', color: 'bg-purple-100', description: '+10% interest weekly!' },
];

const VaultPage = () => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [vault, setVault] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transferMode, setTransferMode] = useState(null); // {from: 'coins', to: 'spend'}
  const [transferAmount, setTransferAmount] = useState('');
  const [showDreamGoal, setShowDreamGoal] = useState(false);
  const [dreamGoal, setDreamGoal] = useState({ name: '', target: '' });

  useEffect(() => {
    fetchVault();
  }, []);

  const fetchVault = async () => {
    try {
      const response = await api.get('/coin-world/vault');
      setVault(response.data);
    } catch (error) {
      toast.error('Could not load vault');
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferAmount || parseInt(transferAmount) <= 0) {
      toast.error('Enter a valid amount!');
      return;
    }

    try {
      const response = await api.post('/coin-world/vault/transfer', {
        from_jar: transferMode.from,
        to_jar: transferMode.to,
        amount: parseInt(transferAmount)
      });
      
      toast.success(response.data.message);
      
      if (response.data.dream_complete) {
        toast.success('🎉 Dream Goal Complete!', { duration: 5000 });
      }
      
      await fetchVault();
      await refreshUser();
      setTransferMode(null);
      setTransferAmount('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed!');
    }
  };

  const handleSetDreamGoal = async () => {
    if (!dreamGoal.name || !dreamGoal.target || parseInt(dreamGoal.target) <= 0) {
      toast.error('Fill in your dream goal!');
      return;
    }

    try {
      await api.post('/coin-world/vault/set-dream-goal', {
        name: dreamGoal.name,
        target_amount: parseInt(dreamGoal.target)
      });
      
      toast.success(`Dream goal set: ${dreamGoal.name}!`);
      await fetchVault();
      setShowDreamGoal(false);
      setDreamGoal({ name: '', target: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not set goal!');
    }
  };

  const getJarBalance = (jarId) => {
    if (jarId === 'coins') return vault?.coins || 0;
    return vault?.vault?.[`${jarId}_jar`] || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl animate-bounce-soft mb-4">🏦</div>
          <p className="text-xl text-slate-600">Opening your vault...</p>
        </div>
      </div>
    );
  }

  const dreamGoalData = vault?.vault?.dream_goal;
  const dreamJarAmount = vault?.vault?.dream_jar || 0;
  const dreamProgress = dreamGoalData ? (dreamJarAmount / dreamGoalData.target_amount) * 100 : 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="vault-page">
      {/* Header */}
      <header className="bg-white border-b-2 border-slate-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/coin-world')}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-slate-600" />
            </button>
            <h1 className="text-xl font-bold text-slate-900 font-fredoka">My Coin Vault</h1>
          </div>
          
          <div className="flex items-center gap-2 bg-primary/20 px-4 py-2 rounded-full">
            <Coins className="w-5 h-5 text-primary" />
            <span className="font-bold text-slate-900">{vault?.coins || 0}</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Available Coins */}
        <div className="card-bubbly bg-gradient-to-br from-primary/20 to-primary/10 text-center">
          <p className="text-sm text-slate-600 mb-1">Available Coins</p>
          <p className="text-4xl font-black text-slate-900">{vault?.coins || 0}</p>
          <p className="text-sm text-slate-500 mt-1">Tap a jar to move coins</p>
        </div>

        {/* Save Streak Info */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🔥</span>
            <div className="flex-1">
              <p className="font-bold text-green-800">
                Save Streak: {vault?.save_streak || 0} days
              </p>
              <p className="text-sm text-green-600">
                {vault?.growth_rate} - Keep saving to grow your coins!
              </p>
            </div>
          </div>
          {vault?.growth_applied > 0 && (
            <div className="mt-2 text-center bg-green-100 rounded-xl p-2">
              <p className="font-bold text-green-700">+{vault.growth_applied} coins grew today! 🌱</p>
            </div>
          )}
        </div>

        {/* Jars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {JARS.map((jar) => {
            const balance = getJarBalance(jar.id);
            const isSelected = transferMode?.from === jar.id || transferMode?.to === jar.id;
            
            return (
              <div
                key={jar.id}
                className={`card-bubbly ${jar.color} ${isSelected ? 'ring-4 ring-primary' : ''}`}
                data-testid={`jar-${jar.id}`}
              >
                <div className="text-center mb-4">
                  <div className="text-5xl mb-2">{jar.emoji}</div>
                  <h3 className="font-bold text-slate-900">{jar.name}</h3>
                  <p className="text-sm text-slate-600">{jar.description}</p>
                </div>
                
                <div className="text-center mb-4">
                  <p className="text-3xl font-black text-slate-900">{balance}</p>
                  <p className="text-sm text-slate-500">coins</p>
                </div>

                {/* Dream Jar Goal Progress */}
                {jar.id === 'dream' && dreamGoalData && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-600">{dreamGoalData.name}</span>
                      <span className="font-bold">{dreamJarAmount}/{dreamGoalData.target_amount}</span>
                    </div>
                    <Progress value={dreamProgress} className="h-3 bg-slate-200" />
                    {dreamProgress >= 100 && (
                      <p className="text-center text-green-600 font-bold mt-2">
                        🎉 Goal Reached!
                      </p>
                    )}
                  </div>
                )}

                {/* Dream Jar Set Goal Button */}
                {jar.id === 'dream' && !dreamGoalData && (
                  <button
                    onClick={() => setShowDreamGoal(true)}
                    className="w-full py-2 bg-purple-200 text-purple-800 rounded-xl font-bold text-sm mb-3"
                  >
                    Set a Dream Goal ⭐
                  </button>
                )}

                {/* Transfer Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setTransferMode({ from: 'coins', to: jar.id })}
                    className="flex-1 py-2 bg-white/80 hover:bg-white rounded-xl font-bold text-sm text-slate-700"
                  >
                    Add +
                  </button>
                  {balance > 0 && (
                    <button
                      onClick={() => setTransferMode({ from: jar.id, to: 'coins' })}
                      className={`flex-1 py-2 rounded-xl font-bold text-sm ${
                        (jar.id === 'dream' && dreamGoalData && dreamJarAmount < dreamGoalData.target_amount) ||
                        (jar.id === 'spend' && !vault?.can_withdraw_spend)
                          ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                          : 'bg-white/80 hover:bg-white text-slate-700'
                      }`}
                      disabled={
                        (jar.id === 'dream' && dreamGoalData && dreamJarAmount < dreamGoalData.target_amount) ||
                        (jar.id === 'spend' && !vault?.can_withdraw_spend)
                      }
                    >
                      Take -
                    </button>
                  )}
                </div>

                {/* Dream Jar Lock Note */}
                {jar.id === 'dream' && dreamGoalData && dreamJarAmount < dreamGoalData.target_amount && (
                  <p className="text-xs text-center text-purple-600 mt-2 flex items-center justify-center gap-1">
                    <Lock className="w-3 h-3" /> Locked until goal reached
                  </p>
                )}

                {/* Spend Jar Daily Limit Note */}
                {jar.id === 'spend' && !vault?.can_withdraw_spend && balance > 0 && (
                  <p className="text-xs text-center text-blue-600 mt-2 flex items-center justify-center gap-1">
                    <Lock className="w-3 h-3" /> Already withdrawn today
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Transfer Modal */}
        {transferMode && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-6 w-full max-w-sm animate-pop">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <span className="text-3xl">
                    {transferMode.from === 'coins' ? '🪙' : JARS.find(j => j.id === transferMode.from)?.emoji}
                  </span>
                  <ArrowRight className="w-6 h-6 text-slate-400" />
                  <span className="text-3xl">
                    {transferMode.to === 'coins' ? '🪙' : JARS.find(j => j.id === transferMode.to)?.emoji}
                  </span>
                </div>
                <h3 className="font-bold text-lg text-slate-900">
                  Move Coins
                </h3>
                <p className="text-sm text-slate-500">
                  Available: {getJarBalance(transferMode.from)} coins
                </p>
              </div>

              <input
                type="number"
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                placeholder="How many coins?"
                className="w-full h-14 rounded-2xl border-2 border-slate-200 px-4 text-center text-2xl font-bold mb-4"
                min="1"
                max={getJarBalance(transferMode.from)}
                data-testid="transfer-amount-input"
              />

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setTransferMode(null);
                    setTransferAmount('');
                  }}
                  className="flex-1 py-3 bg-slate-100 rounded-2xl font-bold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTransfer}
                  className="flex-1 py-3 bg-primary rounded-2xl font-bold text-black"
                  data-testid="confirm-transfer-btn"
                >
                  Move Coins
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Set Dream Goal Modal */}
        {showDreamGoal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-6 w-full max-w-sm animate-pop">
              <div className="text-center mb-6">
                <div className="text-5xl mb-3">⭐</div>
                <h3 className="font-bold text-xl text-slate-900">Set Your Dream Goal</h3>
                <p className="text-sm text-slate-500">What are you saving for?</p>
              </div>

              <input
                type="text"
                value={dreamGoal.name}
                onChange={(e) => setDreamGoal(prev => ({ ...prev, name: e.target.value }))}
                placeholder="My dream is..."
                className="w-full h-14 rounded-2xl border-2 border-slate-200 px-4 text-lg mb-3"
                data-testid="dream-name-input"
              />

              <input
                type="number"
                value={dreamGoal.target}
                onChange={(e) => setDreamGoal(prev => ({ ...prev, target: e.target.value }))}
                placeholder="How many coins?"
                className="w-full h-14 rounded-2xl border-2 border-slate-200 px-4 text-lg mb-4"
                min="10"
                data-testid="dream-target-input"
              />

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDreamGoal(false)}
                  className="flex-1 py-3 bg-slate-100 rounded-2xl font-bold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSetDreamGoal}
                  className="flex-1 py-3 bg-purple-500 rounded-2xl font-bold text-white"
                  data-testid="set-dream-goal-btn"
                >
                  Set Goal
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tips - How Each Jar Works */}
        <div className="card-bubbly bg-gradient-to-br from-blue-50 to-indigo-50">
          <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2 text-lg">
            <Info className="w-5 h-5 text-blue-500" />
            How Each Jar Works
          </h3>
          
          {/* Dream Jar */}
          <div className="bg-purple-100/70 rounded-xl p-4 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">⭐</span>
              <h4 className="font-bold text-purple-800">Dream Jar</h4>
            </div>
            <ul className="space-y-1.5 text-sm text-purple-900">
              <li className="flex items-start gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span><strong>+10% interest</strong> added every week!</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>Set a dream goal and watch your savings grow</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span><strong>Locked</strong> until you reach your goal - helps you save!</span>
              </li>
            </ul>
          </div>

          {/* Save Jar */}
          <div className="bg-green-100/70 rounded-xl p-4 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">🏦</span>
              <h4 className="font-bold text-green-800">Save Jar</h4>
            </div>
            <ul className="space-y-1.5 text-sm text-green-900">
              <li className="flex items-start gap-2">
                <span className="text-green-500 font-bold">•</span>
                <span><strong>+5% interest</strong> added every 3 days!</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 font-bold">•</span>
                <span><strong>2 coin fee</strong> when you take money out</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 font-bold">•</span>
                <span>The fee teaches you to think before spending!</span>
              </li>
            </ul>
          </div>

          {/* Spend Jar */}
          <div className="bg-blue-100/70 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">💰</span>
              <h4 className="font-bold text-blue-800">Spend Jar</h4>
            </div>
            <ul className="space-y-1.5 text-sm text-blue-900">
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold">•</span>
                <span>For coins you want to use soon</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold">•</span>
                <span><strong>+1 bonus coin</strong> for every 10 coins you add!</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold">•</span>
                <span><strong>1 withdrawal per day</strong> - plan your spending wisely!</span>
              </li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VaultPage;
